<script lang="ts">
  import { onMount } from 'svelte';
  import { push } from 'svelte-spa-router';
  import Button from '../../../components/Button.svelte';
  import BackButton from '../../../components/BackButton.svelte';
  import Table from '../../../components/Table.svelte';
  import Pagination from '../../../components/Pagination.svelte';
  import { CharityDonationRequestService, type CharityDonationRequest, type PaginationInfo } from '../../../lib/api/charityServices';
  import { PayoutService, type DonationPayout } from '../../../lib/api/charityServices';
  import { formatDate } from '../../../lib/utils/formatters';
  import { setCurrentDonation } from '../../../lib/stores/donation-request.store';
  import {
    getCharityDonationListStatus,
    isPaidOutToCharity,
  } from '../../../lib/utils/charity-donation-status';

  export let employerId: string = '';
  
  let loading = false;
  let error: string | null = null;
  let employer: any = null;
  let donations: CharityDonationRequest[] = [];
  let completedPayouts: DonationPayout[] = [];
  let currentEmployerId: string = '';
  let isLoading = false;

  /** 'completed' | 'incoming' */
  let donationsTab: 'completed' | 'incoming' = 'completed';
  
  // Pagination: donations (incoming) and payouts (completed) separately
  let currentPage = 1;
  let itemsPerPage = 20;
  let paging: PaginationInfo | null = null;
  let payoutsPaging: PaginationInfo | null = null;

  async function loadEmployerDetails(id: string) {
    if (!id) {
      error = 'No employer ID provided';
      loading = false;
      isLoading = false;
      return;
    }

    if (isLoading || (currentEmployerId === id && employer !== null)) {
      return;
    }

    isLoading = true;
    currentEmployerId = id;
    loading = true;
    error = null;
    
    try {
      const [donationsResult, payoutsResult] = await Promise.all([
        CharityDonationRequestService.getDonationRequests({
          employer_uuid: id,
          page: currentPage,
          per_page: itemsPerPage,
        }),
        PayoutService.getPayouts({
          employer: id,
          page: 1,
          per_page: 100,
        }),
      ]);

      if (donationsResult.success && donationsResult.donations) {
        donations = donationsResult.donations;
        paging = donationsResult.paging || null;
      } else {
        donations = [];
        paging = null;
      }

      if (payoutsResult.success && payoutsResult.payouts) {
        completedPayouts = payoutsResult.payouts.filter(
          (p) => (p.donation_payout_status?.name || p.status?.name || '').toLowerCase() === 'completed'
        );
        payoutsPaging = payoutsResult.paging || null;
      } else {
        completedPayouts = [];
        payoutsPaging = null;
      }

      const first = donations[0];
      const firstPayout = completedPayouts[0];
      if (first?.employer) {
        employer = {
          uuid: first.employer.uuid,
          name: first.employer.name || 'Unknown Employer',
          abn: first.employer.abn,
          contact_email: first.employer.contact_email,
          contact_phone: first.employer.contact_phone,
          contact_name: first.employer.contact_name,
          head_office_location: first.employer.head_office_location,
        };
      } else if (first) {
        employer = { uuid: id, name: first.employer_name || 'Unknown Employer' };
      } else if (firstPayout) {
        employer = {
          uuid: id,
          name: firstPayout.company || firstPayout.employer?.name || 'Unknown Employer',
        };
      } else {
        employer = { uuid: id, name: 'Unknown Employer' };
      }

      if (!donationsResult.success && donations.length === 0) {
        error = donationsResult.error || 'Failed to load employer donations';
      }
    } catch (err: any) {
      console.error('Error loading employer details:', err);
      error = err.message || 'Failed to load employer details';
      donations = [];
      completedPayouts = [];
      paging = null;
      payoutsPaging = null;
    } finally {
      loading = false;
      isLoading = false;
    }
  }

  function formatCurrency(value: number | undefined): string {
    if (value === undefined) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  }

  function getDonationDisplayStatus(row: CharityDonationRequest): string {
    return getCharityDonationListStatus(row);
  }

  function getDonationStatusClass(displayStatus: string): string {
    switch (displayStatus) {
      case 'Pending': return 'incoming';
      case 'Confirmed Received': return 'received';
      case 'Pending Receival Confirmation': return 'pending-receival';
      default: return 'incoming';
    }
  }

  function getDonationStatusIcon(displayStatus: string): string {
    switch (displayStatus) {
      case 'Pending':
        return '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M19 15v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zM4 7v2h12V7H4zm0 4v2h8v-2H4z"/></svg>';
      case 'Confirmed Received':
        return '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>';
      case 'Pending Receival Confirmation':
        return '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"/><path d="M12.5 7H11v6l5.25 3.15.75-1.23-4.5-2.67z"/></svg>';
      default:
        return '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M19 15v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zM4 7v2h12V7H4zm0 4v2h8v-2H4z"/></svg>';
    }
  }

  function handleDonationClick(donation: CharityDonationRequest) {
    if (donation.payout_uuid) {
      push(`/payout-detail/${donation.payout_uuid}`);
    } else if (donation.uuid) {
      setCurrentDonation(donation);
      push(`/donation-request/${donation.uuid}`);
    }
  }

  function handlePageChange(event: CustomEvent<{page: number}>) {
    currentPage = event.detail.page;
    loadEmployerDetails(employerId);
  }

  // Total amount for a donation (leave value + employer top-up if present)
  function getDonationAmount(row: CharityDonationRequest & { employer_top_up?: number }): number {
    const leave = typeof row.leave_value === 'number' ? row.leave_value : parseFloat(String(row.leave_value || row.value || 0)) || 0;
    const topUp = typeof row.employer_top_up === 'number' ? row.employer_top_up : parseFloat(String(row.employer_top_up || 0)) || 0;
    return leave + topUp;
  }

  // --- Completed tab: payouts (payments) ---
  function getPayoutDisplayStatus(p: DonationPayout): string {
    const status = (p.donation_payout_status?.name || p.status?.name || '').toUpperCase();
    if (status === 'COMPLETED') {
      return p.received || p.charity_marked_received_at ? 'Confirmed Received' : 'Pending Receival Confirmation';
    }
    return p.donation_payout_status?.name || p.status?.name || 'Pending';
  }

  function transformCompletedPayoutsToRows(): Array<{ uuid: string; date: string; company: string; net_payment: number; displayStatus: string }> {
    return completedPayouts.map((p) => ({
      uuid: p.uuid,
      date: formatDate(p.transaction_date) || '—',
      company: p.company || p.employer?.name || employer?.name || '—',
      net_payment: (p.net_payment ?? parseFloat(String(p.total_amount || 0))) || 0,
      displayStatus: getPayoutDisplayStatus(p),
    }));
  }

  const completedPaymentsColumns = [
    { key: 'date', label: 'Date', width: '22%', render: (_: any, row: any) => `<span class="col-date">${row.date}</span>` },
    { key: 'company', label: 'Company', width: '28%', render: (_: any, row: any) => `<span class="col-company">${row.company}</span>` },
    { key: 'net_payment', label: 'Amount', width: '12%', render: (_: any, row: any) => `<span class="col-amount">${formatCurrency(row.net_payment)}</span>` },
    {
      key: 'displayStatus',
      label: 'Confirmation Status',
      width: '38%',
      align: 'center' as const,
      render: (_: any, row: any) => {
        const statusClass = getDonationStatusClass(row.displayStatus);
        const icon = getDonationStatusIcon(row.displayStatus);
        return `<div class="donation-status-badge status-${statusClass}">${icon}<span>${row.displayStatus}</span></div>`;
      },
    },
  ];

  function handleCompletedRowClick(row: { uuid: string }) {
    if (row?.uuid) push(`/payout-detail/${row.uuid}`);
  }

  // Incoming tab: donations list (Date, Company, Amount, Confirmation Status)
  const columns = [
    {
      key: 'date',
      label: 'Date',
      width: '22%',
      render: (_: any, row: CharityDonationRequest) => {
        return `<span class="col-date">${formatDate(row.created_at || row.date) || '—'}</span>`;
      }
    },
    {
      key: 'company',
      label: 'Company',
      width: '28%',
      render: (_: any, row: CharityDonationRequest) => {
        const name = row.employer?.name || row.employer_name || employer?.name || 'N/A';
        return `<span class="col-company">${name}</span>`;
      }
    },
    {
      key: 'amount',
      label: 'Amount',
      width: '12%',
      render: (_: any, row: CharityDonationRequest) => {
        return `<span class="col-amount">${formatCurrency(getDonationAmount(row))}</span>`;
      }
    },
    {
      key: 'confirmation_status',
      label: 'Confirmation Status',
      width: '38%',
      align: 'center' as const,
      render: (_: any, row: CharityDonationRequest) => {
        const displayStatus = getDonationDisplayStatus(row);
        const statusClass = getDonationStatusClass(displayStatus);
        const icon = getDonationStatusIcon(displayStatus);
        return `<div class="donation-status-badge status-${statusClass}">${icon}<span>${displayStatus}</span></div>`;
      }
    }
  ];

  $: incomingDonations = donations.filter((d) => !isPaidOutToCharity(d));

  function handleRowClick(row: any) {
    const donation = donations.find(d => d.uuid === row.uuid);
    if (donation) handleDonationClick(donation);
  }

  // Load employer when employerId changes
  $: {
    if (employerId && employerId !== currentEmployerId) {
      error = null;
      employer = null;
      donations = [];
      currentPage = 1;
      loadEmployerDetails(employerId);
    } else if (!employerId && !loading && !employer) {
      if (currentEmployerId !== '') {
        error = 'No employer ID provided';
      }
    }
  }

  // Also try to load on mount as fallback
  onMount(() => {
    if (employerId && !employer && !loading) {
      loadEmployerDetails(employerId);
    }
  });
</script>

<div class="employer-detail-page">
  <BackButton text="Back" onClick={() => window.history.back()} />
  
  {#if loading}
    <div class="loading-container">
      <div class="loading-spinner"></div>
      <p>Loading employer donations...</p>
    </div>
  {:else if error}
    <div class="error-container">
      <p class="error-message">{error}</p>
      <Button variant="primary" on:click={() => loadEmployerDetails(employerId)}>Try Again</Button>
    </div>
  {:else if employer}
    <div class="detail-container">
      <!-- Employer Information Section -->
      <div class="info-section">
        <h2>Employer Information</h2>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">Employer Name</span>
            <span class="value-large">{employer.name || 'N/A'}</span>
          </div>
          {#if employer.abn}
            <div class="info-item">
              <span class="info-label">ABN</span>
              <span>{employer.abn}</span>
            </div>
          {/if}
          {#if employer.contact_name}
            <div class="info-item">
              <span class="info-label">Contact Name</span>
              <span>{employer.contact_name}</span>
            </div>
          {/if}
          {#if employer.contact_email}
            <div class="info-item">
              <span class="info-label">Contact Email</span>
              <span>
                <a href="mailto:{employer.contact_email}" class="email-link">
                  {employer.contact_email}
                </a>
              </span>
            </div>
          {/if}
          {#if employer.contact_phone}
            <div class="info-item">
              <span class="info-label">Contact Phone</span>
              <span>
                <a href="tel:{employer.contact_phone}" class="phone-link">
                  {employer.contact_phone}
                </a>
              </span>
            </div>
          {/if}
          {#if employer.head_office_location}
            <div class="info-item">
              <span class="info-label">Head Office Location</span>
              <span>{employer.head_office_location}</span>
            </div>
          {/if}
        </div>
      </div>

      <!-- Donations Section (tabbed) -->
      <div class="info-section">
        <div class="donations-tabs" role="tablist" aria-label="Donations">
          <button
            type="button"
            role="tab"
            class="donations-tab"
            class:active={donationsTab === 'completed'}
            on:click={() => (donationsTab = 'completed')}
            aria-selected={donationsTab === 'completed'}
            aria-controls="donations-panel-completed"
            id="tab-completed"
          >
            Completed Payments ({completedPayouts.length})
          </button>
          <button
            type="button"
            role="tab"
            class="donations-tab"
            class:active={donationsTab === 'incoming'}
            on:click={() => (donationsTab = 'incoming')}
            aria-selected={donationsTab === 'incoming'}
            aria-controls="donations-panel-incoming"
            id="tab-incoming"
          >
            Incoming Donations ({incomingDonations.length})
          </button>
        </div>

        <div
          id="donations-panel-completed"
          role="tabpanel"
          aria-labelledby="tab-completed"
          class="donations-panel"
          hidden={donationsTab !== 'completed'}
        >
          {#if donationsTab === 'completed'}
            {#if completedPayouts.length > 0}
              <div class="table-section">
                {#key completedPayouts}
                  <Table
                    columns={completedPaymentsColumns}
                    data={transformCompletedPayoutsToRows()}
                    onRowClick={handleCompletedRowClick}
                  />
                {/key}
              </div>
            {:else}
              <div class="no-data-container">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" class="no-data-icon">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                </svg>
                <h3>No Completed Payments</h3>
                <p>This employer has no completed payouts yet.</p>
              </div>
            {/if}
          {/if}
        </div>

        <div
          id="donations-panel-incoming"
          role="tabpanel"
          aria-labelledby="tab-incoming"
          class="donations-panel"
          hidden={donationsTab !== 'incoming'}
        >
          {#if donationsTab === 'incoming'}
            {#if incomingDonations.length > 0}
              <div class="table-section">
                <Table
                  columns={columns}
                  data={incomingDonations}
                  onRowClick={handleRowClick}
                />
              </div>
              {#if paging && paging.last_page > 1}
                <div class="pagination-container">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={paging.last_page}
                    on:pageChange={handlePageChange}
                  />
                </div>
              {/if}
            {:else}
              <div class="no-data-container">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" class="no-data-icon">
                  <path d="M19 15v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zM4 7v2h12V7H4zm0 4v2h8v-2H4z"></path>
                </svg>
                <h3>No Incoming Donations</h3>
                <p>There are no incoming (pending/approved) donations for this employer.</p>
              </div>
            {/if}
          {/if}
        </div>
      </div>
    </div>
  {/if}
</div>

<style>
  .employer-detail-page {
    padding: 1.5rem;
    max-width: 1000px;
    margin: 0 auto;
    background: #f7fafc;
    min-height: 100vh;
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .loading-spinner {
    width: 2rem;
    height: 2rem;
    border: 3px solid #f3f4f6;
    border-top: 3px solid var(--color-primary, #f0b429);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .error-message {
    color: var(--color-negative, #dc2626);
    font-size: 1.1rem;
    margin-bottom: 1rem;
  }

  .detail-container {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .info-section {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }

  .donations-tabs {
    display: flex;
    gap: 0;
    border-bottom: 2px solid #e5e7eb;
    margin-bottom: 1.5rem;
  }

  .donations-tab {
    padding: 0.75rem 1.25rem;
    font-size: 1rem;
    font-weight: 500;
    color: #6b7280;
    background: none;
    border: none;
    border-bottom: 3px solid transparent;
    margin-bottom: -2px;
    cursor: pointer;
    transition: color 0.2s, border-color 0.2s;
  }

  .donations-tab:hover {
    color: #111827;
  }

  .donations-tab.active {
    color: var(--color-primary, #f0b429);
    border-bottom-color: var(--color-primary, #f0b429);
  }

  .donations-panel {
    margin-top: 0;
  }

  .donations-panel[hidden] {
    display: none;
  }

  .info-section h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 1.5rem 0;
  }

  .info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1rem;
  }

  .info-item {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .info-item .info-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
  }

  .info-item span {
    font-size: 1rem;
    color: #111827;
    font-weight: 400;
  }

  .value-large {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
  }

  .email-link,
  .phone-link {
    color: var(--color-primary, #3b82f6);
    text-decoration: none;
    transition: color 0.2s;
  }

  .email-link:hover,
  .phone-link:hover {
    color: var(--color-primary-hover, #2563eb);
    text-decoration: underline;
  }

  .table-section {
    min-width: 0; /* Allow shrink so table doesn't force horizontal scroll */
    overflow: hidden;
    margin-top: 1rem;
    border-radius: 12px;
  }

  :global(.col-date) { color: #6b7280; }
  :global(.col-company) { font-weight: 600; color: #111827; }
  :global(.col-amount) { font-weight: 600; color: #111827; }

  /* Donation status badges (Incoming, Confirmed Received, Pending Receival Confirmation) */
  :global(.donation-status-badge) {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.875rem;
    font-weight: 500;
    gap: 0.375rem;
    white-space: nowrap;
  }

  :global(.donation-status-badge svg) {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  :global(.donation-status-badge.status-incoming) {
    background-color: #fff8e1;
    color: #f57c00;
  }

  :global(.donation-status-badge.status-received) {
    background-color: #d1fae5;
    color: #065f46;
  }

  :global(.donation-status-badge.status-pending-receival) {
    background-color: #dbeafe;
    color: #1e40af;
  }

  .pagination-container {
    display: flex;
    justify-content: center;
    margin-top: 1.5rem;
  }

  .no-data-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 2rem;
    text-align: center;
  }

  .no-data-icon {
    color: #d1d5db;
    margin-bottom: 1.5rem;
  }

  .no-data-container h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin-bottom: 0.5rem;
  }

  .no-data-container p {
    color: #6b7280;
    margin-bottom: 2rem;
    max-width: 400px;
    font-size: 0.875rem;
  }

  @media (max-width: 768px) {
    .employer-detail-page {
      padding: 1rem;
    }

    .info-grid {
      grid-template-columns: 1fr;
    }
  }
</style>

